import {
  Container,
  Input,
  CustomDateInput,
  Dropdown,
  Button,
} from '@/components';
import { useMinMaxContext } from '@/context/minMaxContext/MinMaxContextProvider';
import { useSalesFileContext } from '@/context/salesFileContext/SalesFileContextProvider';
import React, { useEffect, useState, useMemo } from 'react';
import { useProductGroup } from '@/context/ProductGroupContext/ProductGroupContextProvider';
import { FaFileCsv } from 'react-icons/fa';

const MinMaxReportView = () => {
  const {
    minMaxReport,
    setMinMaxReport,
    getAbcClassListAPI,
    abcClassList,
    resetMinMaxReport,
    downloadExcel,
    importForProntoAPI,
  } = useMinMaxContext();

  const { getWhrehouseListAPI, warehouseList } = useSalesFileContext();

  const [selectedWhse, setSelectedWhse] = useState([]);
  const [selectedAbc, setSelectedAbc] = useState([]);
  const [selectedGrp, setSelectedGrp] = useState([]);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [errors, setErrors] = useState({
    fromDate: '',
    toDate: '',
    avgDemand: '',
    mainProductGroupList: '',
    abcClassList: '',
    whseList: '',
    dateDiffErr: '',
  });

  const { powerBiMainProductGroupResponse, getPowerBiMainProductGroup } =
    useProductGroup();

  useEffect(() => {
    //Reset
    resetMinMaxView();
    (async () => {
      await Promise.all([
        getWhrehouseListAPI(),
        getAbcClassListAPI(),
        getPowerBiMainProductGroup(),
      ]);
    })();
  }, []);

  const resetMinMaxView = () => {
    resetMinMaxReport();
    setFromDate('');
    setToDate('');
    setSelectedAbc([]);
    setSelectedWhse([]);
  };

  const warehouseOptions = (warehouseList || []).map((w) => ({
    value: w,
    label: w,
    data: w,
  }));

  const abcOptions = (abcClassList || []).map((a) => ({
    value: a,
    label: a === '' ? 'None' : a,
    data: a,
  }));

  const productGroupOptions = (powerBiMainProductGroupResponse || []).map(
    (g) => ({
      value: g.id,
      label:
        g.powerBiMainProductGroupName === ''
          ? 'None'
          : g.powerBiMainProductGroupName,
      data: g,
    }),
  );

  function convertToYMD(dateStr) {
    if (!dateStr) return '';
    const [day, month, year] = dateStr.split('-');
    return `${year}-${month}-${day}`;
  }

  // Memoize mapped lists for efficiency
  const mappedWhseList = useMemo(
    () => selectedWhse.map((index) => warehouseList[index]),
    [selectedWhse, warehouseList],
  );
  const mappedAbcClassList = useMemo(
    () => selectedAbc.map((index) => abcClassList[index]),
    [selectedAbc, abcClassList],
  );
  const mappedMainProductGroupList = useMemo(
    () => selectedGrp?.map((index) => powerBiMainProductGroupResponse[index]),
    [selectedGrp, powerBiMainProductGroupResponse],
  );

  useEffect(() => {
    setMinMaxReport((prev) => ({
      ...prev,
      whseList: mappedWhseList,
      abcClassList: mappedAbcClassList,
      mainProductGroupList: mappedMainProductGroupList,
      fromDate: convertToYMD(fromDate),
      toDate: convertToYMD(toDate),
    }));
  }, [
    mappedWhseList,
    mappedAbcClassList,
    mappedMainProductGroupList,
    fromDate,
    toDate,
  ]);

  //handling validations
  function validateForm(form) {
    const errors = {};
    // 1. Validate Date Range
    const { fromDate, toDate } = form;

    if (!fromDate || !toDate) {
      errors.fromDate = '* From and To Date are required.';
      errors.toDate = '* From and To Date are required.';
    } else {
      const from = new Date(fromDate);
      const to = new Date(toDate);

      if (isNaN(from) || isNaN(to)) {
        errors.fromDate = '* Invalid date format.';
        errors.toDate = '* Invalid date format.';
      } else if (from >= to) {
        errors.fromDate = '* From date should be less than To date.';
        errors.toDate = '* From date should be less than To date.';
      } else {
        const getMonthDiff = (start, end) => {
          let months =
            (end.getFullYear() - start.getFullYear()) * 12 +
            (end.getMonth() - start.getMonth());
          if (end.getDate() < start.getDate()) months--;
          return months;
        };

        const months = getMonthDiff(from, to);
        if (months < 6 || months > 12) {
          errors.toDate =
            '* Difference must be minimum 6 months & max 12 months.';
        }
      }
    }

    // 2. Validate other fields
    if (!form.whseList || form.whseList.length === 0) {
      errors.whseList = 'Please select at least one warehouse';
    }

    if (!form.abcClassList || form.abcClassList.length === 0) {
      errors.abcClassList = 'Please select at least one ABC class';
    }

    if (!form.mainProductGroupList || form.mainProductGroupList.length === 0) {
      errors.mainProductGroupList = 'Please select at least one product group';
    }

    if (
      form.avgDemand === null ||
      form.avgDemand === undefined ||
      form.avgDemand === '' ||
      isNaN(Number(form.avgDemand))
    ) {
      errors.avgDemand = 'Average demand is required';
    } else if (Number(form.avgDemand) < 0 || Number(form.avgDemand) > 1) {
      errors.avgDemand = 'should be between 0 to 1';
    }

    return errors;
  }

  //convert response blob to file and trigger download
  const handleDownload = () => {
    const validationErrors = validateForm(minMaxReport); // form is your full form state object
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      {
        downloadExcel(minMaxReport);
      }
    }
  };

  const handleImportPronto = () => {
    const validationErrors = validateForm(minMaxReport);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      importForProntoAPI(minMaxReport);
    }
  };

  return (
    <Container title={'Min-Max Report'}>
      <div className="p-4 grid grid-cols-3 gap-x-4 gap-y-6 w-full">
        <div className="flex flex-col space-y-1">
          <Dropdown
            multiSelect
            label="Warehouse(s)"
            options={warehouseOptions}
            selected={selectedWhse}
            onChange={setSelectedWhse}
            placeholder="Select warehouse(s)…"
            error={errors.whseList}
          />
        </div>

        <div className="flex flex-col space-y-1">
          <Dropdown
            multiSelect
            label="ABC Class(es)"
            options={abcOptions}
            selected={selectedAbc}
            onChange={setSelectedAbc}
            placeholder="Select ABC class(es)…"
            error={errors.abcClassList}
          />
        </div>

        <div className="flex flex-col space-y-1">
          <Dropdown
            multiSelect
            label="Main Product Group(s)"
            options={productGroupOptions}
            selected={selectedGrp}
            onChange={setSelectedGrp}
            placeholder="Select Main Product Group"
            error={errors.mainProductGroupList}
          />
        </div>

        <div className="flex flex-col space-y-1">
          <Input
            type="number"
            value={minMaxReport.avgDemand}
            label="Average Demand"
            onChange={(e) => {
              if (e.target.value === '') {
                setMinMaxReport((prev) => ({ ...prev, avgDemand: '' }));
                return;
              }
              setMinMaxReport((prev) => ({
                ...prev,
                avgDemand: e.target.value,
              }));
              validateForm('avgDemand', e.target.value);
            }}
            className="w-full"
            error={errors.avgDemand}
          />
        </div>

        <div className="flex flex-col space-y-1">
          <CustomDateInput
            label="From Date"
            name="fromDate"
            value={fromDate}
            onChange={(date) => {
              // Format date as YYYY-MM-DD
              const formatted =
                date instanceof Date
                  ? date.toISOString().slice(0, 10)
                  : typeof date === 'string' && date.length >= 10
                    ? date.slice(0, 10)
                    : date;
              setFromDate(formatted);
            }}
            className="w-full"
            error={errors.fromDate}
            maxDate={new Date()}
          />
        </div>

        <div className="flex flex-col space-y-1">
          <CustomDateInput
            label="To Date"
            name="toDate"
            value={toDate}
            onChange={(date) => {
              const formatted =
                typeof date === 'string'
                  ? date.slice(0, 10)
                  : date?.toISOString()?.slice(0, 10);
              setToDate(formatted);
              validateForm('toDate', formatted);
            }}
            className="w-full"
            error={errors.toDate}
            maxDate={new Date()}
          />
        </div>
      </div>
      <div className="flex my-5 gap-5 justify-center">
        <Button
          className="w-80 bg-green-400 hover:bg-green-500 text-white py-3 rounded-md text-base font-medium flex items-center justify-center transition"
          onClick={handleDownload}>
          <FaFileCsv className="w-5 h-5 mr-2" />
          Download Min-Max Report
        </Button>
        <Button
          className="w-80 bg-green-400 hover:bg-green-500 text-white py-3 rounded-md text-base font-medium flex items-center justify-center transition"
          onClick={handleImportPronto}>
          <FaFileCsv className="w-5 h-5 mr-2" />
          Import for pronto
        </Button>

        <Button
          className="w-80 bg-gray-200 hover:bg-gray-300 text-black py-3 rounded-md text-base font-medium flex items-center justify-center transition"
          onClick={resetMinMaxView}>
          Cancel
        </Button>
      </div>
    </Container>
  );
};

export default MinMaxReportView;
